import { buildSectionSelector, normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, onDocumentReady } from '@theme/utilities';

const pendingHydrations = new WeakSet();

/**
 * Hydrates a section using the Section Rendering API preserving states.
 * Only updates elements with data-hydration-key attribute. This ensures that non-targeted nodes do not have their markup/state updated unnecessarily.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
async function hydrateSection(sectionId, url) {
  const normalizedId = normalizeSectionId(sectionId);
  const section = document.getElementById(buildSectionSelector(normalizedId));

  if (!section || section.dataset.hydrated === 'true' || pendingHydrations.has(section)) {
    return;
  }

  pendingHydrations.add(section);
  try {
    await sectionRenderer.renderSection(normalizedId, {
      cache: false,
      url,
      mode: 'hydration',
      onRendered: () => {
        if (section.isConnected) section.dataset.hydrated = 'true';
      },
    });
  } finally {
    pendingHydrations.delete(section);
  }
}

/**
 * Hydrates a section using the Section Rendering API preserving states, when
 * the DOM is ready.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
export async function hydrate(sectionId, url) {
  onDocumentReady(() => {
    requestIdleCallback(() => {
      hydrateSection(sectionId, url).catch((error) => console.error('Section hydration failed:', error));
    });
  });
}
